"use strict";

// A function to aquire a fast, locally unique identifier.
// This is NOT crytographically secure.
function fastUniqueId() {
    return Math.floor(0xffffffff * Math.random()).toString(36) + Date.now().toString(36);
}

class MessageManager {
    // The target of the messages can be a worker, a worker scope, or a message port.
    constructor(_target, _receiver, _origin, _cb) {

        this._origin = _origin;
        this._cb = _cb;
        this._target = _target;
        this._receiver = _receiver;
        this._replyPromises = {};
        this._messageCallback = (m) => this.gotMessage(m);
        this.postMessageToTarget = (message, transferable) => this._target.postMessage(message, "*", transferable);
  
        // Make a function to create unique sequence IDs
        // This should be as fast as possible.
        this.generateSeqId = () => (0, fastUniqueId)();
        (this._receiver || this._target).addEventListener("message", this._messageCallback);
    }

    static get TX_SEQUENCE_ID_KEY() { return "_txSequenceId"; }
    static get RX_SEQUENCE_ID_KEY() { return "_rxSequenceId"; }

    dispose() {
        (this._receiver || this._target).removeEventListener("message", this._messageCallback);
        for (let s of Object.values(this._replyPromises)) {
            s.resolve();
        }        
    }

    canReply(message) {
        return message[MessageManager.TX_SEQUENCE_ID_KEY] != null;
    }

    reply(trackable, message, transferable) {
        let sequenceId;
        if (typeof trackable === "string") {
            sequenceId = trackable;
        }
        else if (trackable != null) {
            sequenceId = trackable[MessageManager.TX_SEQUENCE_ID_KEY];
        }
        message[MessageManager.RX_SEQUENCE_ID_KEY] = sequenceId;
        this.postMessage(message, transferable);
    }

    processMessage(data) {
        let seq = data[MessageManager.RX_SEQUENCE_ID_KEY];        
        if (seq) {
            let prom = this._replyPromises[seq];
            if (prom) {
                delete this._replyPromises[seq];
                if (!data.params || data.params.success) {
                    prom.resolve(data.params);
                }
                else {
                    prom.reject();
                }
                return;
            }
        }
        if (this._cb) {
            this._cb(data);
        }
    }

    gotMessage(m) {
        if (m.data && m.origin === this._origin && m.source === this._target) {
            this.processMessage(m.data);
        }
    }

    sendMessage(message, transferable) {

        return new Promise((resolve, reject) => {
            let sequenceId = this.generateSeqId();
            message[MessageManager.TX_SEQUENCE_ID_KEY] = sequenceId;
            this._replyPromises[sequenceId] = { resolve: resolve, reject: reject };
            this.postMessage(message, transferable);
        })
    }

    postMessage(message, transferable) {
        try {
            this.postMessageToTarget(message, transferable);
        }
        catch (err) {
            console.error("Failed to post message " + err);
        }
    }
}

// A class to manage the player in the iframe.
class Player {    
    // The frame is the iframe element that will contain the player.
    // The url is the URL of the standalone player that will be loaded into the iframe.
    // The authToken is an optional token that can be used to authenticate with AMPP. Otherwise,
    //     the user will have to autheticate manually.
    constructor(_frame, _url, _authToken) {
        this._url = _url || "/scs-standalone-player/";
        this._authToken = _authToken;
        if (this._authToken) {
            this._url = `${this._url}?external-auth=true`;
        }

        // Create the message manager before setting the frame source, as requests for auth tokens will come in immediately.
        if (this._messageManager) {
            this._messageManager.dispose();
            this._messageManager = null;
        }
        this._messageManager = new MessageManager(_frame.contentWindow, window, new URL(this._url, document.location).origin, (message) => this.gotMessage(message));

        _frame.src = this._url;
        this._eventListeners = {};
        this._initialized = false;
        this.selectChannels(["V1", "V2", "A1", "A2", "A3", "A4", "A5", "A6", "A7", "A8", "G1"]);
    }

    gotMessage(message) {
        switch (message.name) {
            case "currentPosition":
                this._currentPosition = message.params;
                this.raiseEvent("position", message.params);
                break;
            case "playState":
                this._currentPlayState = message.params;
                this.raiseEvent("playState", message.params);
                break;
            case "alert":
                this.raiseEvent("alert", message.params);
                break;
            case "loadState":
                this._currentLoadState = message.params.loadState;
                this.raiseEvent("loadState", message.params.loadState);
                if (!this._initialized) {                    
                    this.initialized();
                }
                break;
            case "requestAuth":
                this._messageManager.reply(message, {
                    name: "auth",
                    params: {
                        token: this._authToken
                    }
                });
                break;
        }
    }

    updateAuth(token) {
        this._authToken = token;
        this._messageManager.postMessage({
            name: "auth",
            params: {
                token: token
            }
        });
    }

    raiseEvent(eventName, data) {
        if (Array.isArray(this._eventListeners[eventName])) {
            this._eventListeners[eventName].forEach(l => l(data));
        }
    }

    initialized() {        
        this._initialized = true;
        if (this._config) {
            this.configure(this._config);
            delete this._config;
        }
        this.selectChannels(this._channels);
        if (this._assetId) {
            this.load(this._assetId);
        }        
        this.raiseEvent("initialized");
    }

    configure(config) {
        if (this._initialized) {
            this._messageManager.postMessage({
                name: "configure",
                params: config
            });
        }
        else {
            this._config = config;
        }
    }

    selectChannels(channels) {
        this._channels = channels
        if (this._initialized) {
            this._messageManager.postMessage({
                name: "selectChannels",
                params: { channels: channels }
            });
        }
        this.raiseEvent("channels", this._channels);
    }

    load(assetId) {
        if (this._initialized) {
            this._messageManager.postMessage({
                name: "load",
                params: {
                    assetId: assetId
                }
            });
        }
        else {
            this._assetId = assetId;
        }
    }

    unload() {
        if (this._initialized) {
            this._messageManager.postMessage({
                name: "unload"
            });
        }
        else {
            delete this._assetId;
        }
    }

    async play(speed, startAt = undefined, stopAt = undefined, loopTo = undefined) {
        await this._messageManager.sendMessage({
            name: "play",
            params: {
                playSpeed: speed,
                startAt: startAt,
                stopAt: stopAt,
                loopTo: loopTo
            }
        });
    }

    async stop() {
        await this._messageManager.sendMessage({
            name: "stop"
        });
    }

    async step(frame) {
        await this._messageManager.sendMessage({
            name: "step",
            params: {
                frames: frame
            }
        });
    }

    startScrub() {
        this._scrubbing = true;
    }

    endScrub() {
        this._scrubbing = false;
        this.seek(this._lastScrub);
        this._lastScrub = this._newScrub = undefined;
    }

    scrub(pos) {
        if (this._lastScrub === pos) {
            return;
        }
        this._lastScrub = pos;
        this._messageManager.postMessage({
            name: "seek",
            params: {
                pos: this._lastScrub,
                mode: "scrub"
            }
        })
    }

    async seek(pos) {
        await this._messageManager.sendMessage({
            name: "seek",
            params: {
                pos: pos,
                mode: "cue"
            }
        });
    }

    addEventListener(type, listener) {
        // Call initialized callback immediately if frame already loaded
        if (type === "initialized" && this._initialized && listener) {
            listener();
        }

        if (!this._eventListeners[type]) {
            this._eventListeners[type] = [listener];
        }
        else {
            this._eventListeners[type].push(listener);
        }
    }

    removeEventListener(type, listener) {
        if (this._eventListeners[type]) {
            this._eventListeners[type] = this._eventListeners[type].filter(t => t !== listener);
        }
    }

    async setVolume(v) {
        await this._messageManager.sendMessage({
            name: "setVolume",
            params: {
                volume: v
            }
        });
    }

    async getVolume() {
        var result = await this._messageManager.sendMessage({
            name: "getVolume",
        });
        return result.volume;
    }

    get duration() {
        return this._currentPosition?.duration;
    }

    get currentFrame() {
        return this._currentPosition?.frame;
    }

    get currentSrcTimecode() {
        return this._currentPosition?.srcTimecode;
    }

    get currentDstTimecode() {
        return this._currentPosition?.dstTimecode;
    }

    get currentPosition() {
        return this._currentPosition;
    }

    get channels() {
        return this._channels;
    }

    get isInterlaced() {
        return this._currentPosition?.format?.interlaced;
    }

    get sampleRate() {
        return this._currentPosition?.format?.sampleRate;
    }

    async timecodeToFrame(timecode, mode) {
        var result = await this._messageManager.sendMessage({
            name: "tcToFrame",
            params: {
                pos: timecode,
                mode: mode
            }
        });
        return result.pos;
    }

    async frameToTimecode(frame, mode) {
        var result = await this._messageManager.sendMessage({
            name: "frameToTc",
            params: {
                pos: frame,
                mode: mode
            }
        });
        return result.pos;
    }

    async getVersion() {
        var result = await this._messageManager.sendMessage({
            name: "getVersion"
        });
        return result.version;
    }
}

if (typeof window !== "undefined") {
    window.SCSPlayer = Player;
}

export default Player;